import React from 'react';
import { MacBookDeviceFrame } from '../ui/MacBookDeviceFrame';
import { IPhoneDeviceFrame } from '../ui/iPhoneDeviceFrame';
import { BiolinkPhoneFrame } from '../ui/BiolinkPhoneFrame';
import { Cpu, Smartphone, Link2, ShoppingCart, MessageSquare, Sparkles, Layers, ShieldCheck, Zap } from 'lucide-react';

export const GenesisBentoGrid: React.FC = () => {
  return (
    <section id="genesis-360" className="py-24 px-4 md:px-12 bg-slate-50 text-slate-900 font-sans border-t border-slate-200/60">
      <div className="max-w-7xl mx-auto space-y-16">
         
         {/* SECTION HEADER (GOOGLE STYLE) */}
         <div className="text-center max-w-3xl mx-auto space-y-4">
            <div className="inline-flex items-center gap-2 bg-blue-50 text-[#0061FF] px-4 py-1.5 rounded-full text-xs font-bold border border-blue-200/80">
               <Cpu className="w-3.5 h-3.5" />
               ECOSSISTEMA INTEGRADO GENESIS 360°
            </div>
            
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-black tracking-tight text-slate-900 leading-tight">
               Tudo o que sua empresa precisa em um <span className="bg-gradient-to-r from-[#0061FF] to-blue-800 bg-clip-text text-transparent">único núcleo.</span>
            </h2>

            <p className="text-slate-600 text-base md:text-lg font-normal leading-relaxed">
               Elimine a fragmentação. Unifique seu site, aplicativos de delivery PWA, links na bio, integração com marketplaces e automações de vendas em uma plataforma sincronizada.
            </p>
         </div>

         {/* BENTO BOX GRID CONTAINER */}
         <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            
            {/* BENTO CARD 1 (LARGE FEATURED): GENESIS 360 MACBOOK MOCKUP */}
            <div className="md:col-span-12 bg-white border border-slate-200/80 rounded-[24px] p-6 md:p-10 space-y-8 text-left hover:border-slate-300 transition-all">
               <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div className="space-y-2">
                     <span className="text-xs font-bold uppercase tracking-wider text-[#0061FF]">NÚCLEO CENTRAL</span>
                     <h3 className="text-2xl md:text-3xl font-heading font-black text-slate-900">
                        Painel de Controle Genesis 360°
                     </h3>
                     <p className="text-slate-600 text-sm max-w-xl">
                        Acompanhe vendas, faturamento, leads do Google e pedidos de entrega em tempo real através de um painel inteligente.
                     </p>
                  </div>
                  <div className="flex items-center gap-2">
                     <span className="bg-slate-100 text-slate-700 text-xs font-bold px-3 py-1.5 rounded-xl border border-slate-200">
                        Sincronização 24/7
                     </span>
                  </div>
               </div>

               {/* Integrated MacBook Frame with Live Scroll */}
               <div className="pt-4">
                  <MacBookDeviceFrame title="Painel Operacional YouMachine Genesis 360°" autoScroll={true} />
               </div>
            </div>

            {/* BENTO CARD 2: PWA APPS & DELIVERY */}
            <div className="md:col-span-6 bg-white border border-slate-200/80 rounded-[24px] p-6 md:p-8 space-y-6 text-left hover:border-slate-300 transition-all flex flex-col justify-between">
               <div className="space-y-3">
                  <div className="w-10 h-10 rounded-2xl bg-blue-50 text-[#0061FF] border border-blue-100 flex items-center justify-center font-bold">
                     <Smartphone className="w-5 h-5" />
                  </div>
                  <h3 className="text-2xl font-heading font-black text-slate-900">
                     Aplicativos PWA sem Taxas
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                     Seus clientes instalam seu aplicativo direto no celular sem precisar de App Store. Cardápios interativos, pedidos via WhatsApp e fidelização.
                  </p>
               </div>

               {/* iPhone Frame Display */}
               <div className="pt-4 flex justify-center">
                  <IPhoneDeviceFrame appName="Seu Aplicativo PWA" category="Vendas & Delivery Direto" />
               </div>
            </div>

            {/* BENTO CARD 3: LINKWI BIO LINK 2.0 (WITH PHONE MOCKUP RENDERED) */}
            <div className="md:col-span-6 bg-white border border-slate-200/80 rounded-[24px] p-6 md:p-8 space-y-6 text-left hover:border-slate-300 transition-all flex flex-col justify-between">
               <div className="space-y-3">
                  <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100 flex items-center justify-center font-bold">
                     <Link2 className="w-5 h-5" />
                  </div>
                  <h3 className="text-2xl font-heading font-black text-slate-900">
                     Linkwi • Bio Link 2.0 de Alta Conversão
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                     Transforme a bio do seu Instagram e WhatsApp em um mini-portal com agendamento, formulários, vitrine de produtos e captura de leads.
                  </p>
               </div>

               {/* Biolink Phone Mockup Frame */}
               <div className="pt-4 flex justify-center">
                  <BiolinkPhoneFrame title="Linkwi Bio 2.0" category="Demonstração em Tempo Real" />
               </div>

               {/* Feature Pills */}
               <div className="grid grid-cols-3 gap-2 pt-2">
                  <div className="bg-slate-50 border border-slate-200/80 p-2.5 rounded-xl text-center">
                     <span className="text-[10px] font-bold text-slate-900 block">Captação Leads</span>
                  </div>
                  <div className="bg-slate-50 border border-slate-200/80 p-2.5 rounded-xl text-center">
                     <span className="text-[10px] font-bold text-slate-900 block">WhatsApp Direct</span>
                  </div>
                  <div className="bg-slate-50 border border-slate-200/80 p-2.5 rounded-xl text-center">
                     <span className="text-[10px] font-bold text-slate-900 block">Checkout PWA</span>
                  </div>
               </div>
            </div>

            {/* BENTO CARD 4: MARKETPLACES */}
            <div className="md:col-span-6 bg-white border border-slate-200/80 rounded-[24px] p-6 md:p-8 space-y-4 text-left hover:border-slate-300 transition-all">
               <div className="w-10 h-10 rounded-2xl bg-amber-50 text-amber-600 border border-amber-100 flex items-center justify-center font-bold">
                  <ShoppingCart className="w-5 h-5" />
               </div>
               <h3 className="text-xl font-heading font-black text-slate-900">
                  Hub de Integração com Marketplaces
               </h3>
               <p className="text-slate-600 text-sm leading-relaxed">
                  Conecte seu estoque e catálogo ao Mercado Livre, Shopee, Amazon, Magalu e iFood automaticamente.
               </p>
               <div className="flex flex-wrap gap-2 pt-2">
                  {['Mercado Livre', 'Shopee', 'Amazon', 'Magalu', 'iFood'].map((mkt) => (
                     <span key={mkt} className="bg-slate-100 text-slate-800 text-xs font-bold px-3 py-1.5 rounded-xl border border-slate-200">
                        {mkt}
                     </span>
                  ))}
               </div>
            </div>

            {/* BENTO CARD 5: CRM & AUTOMAÇÃO */}
            <div className="md:col-span-6 bg-white border border-slate-200/80 rounded-[24px] p-6 md:p-8 space-y-4 text-left hover:border-slate-300 transition-all">
               <div className="w-10 h-10 rounded-2xl bg-rose-50 text-rose-600 border border-rose-100 flex items-center justify-center font-bold">
                  <MessageSquare className="w-5 h-5" />
               </div>
               <h3 className="text-xl font-heading font-black text-slate-900">
                  CRM & Atendimento Inteligente
               </h3>
               <p className="text-slate-600 text-sm leading-relaxed">
                  Disparo de mensagens no WhatsApp, pós-venda automático, lembretes de agendamento e histórico de clientes.
               </p>
               <div className="flex items-center gap-2 pt-2">
                  <span className="bg-emerald-50 text-emerald-700 text-xs font-bold px-3 py-1 rounded-lg border border-emerald-200">
                     WhatsApp API Oficial
                  </span>
               </div>
            </div>

         </div>

      </div>
    </section>
  );
};
